import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./MixMatchSectionalBundle.module.css";
import BundlePreviewWrapper, {
    CustomSwatches,
    SubscriptionSelector,
    Swatches,
    VariantSelectorV2,
} from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import { ProductObj, MixMatchSectionalConfigInterface, VariantsObj, mix_match_section } from "../../Types";
import {
    getPriceLabel,
    getFinalPrice,
    getDiscountValueWithSign,
    DiscountTypes,
    fetchCollectionProducts,
    buildMetafieldFromSwatchOptions,
    getDiscountValueWithSignString,
} from "../../constants";
import { TStoreContext } from "../../../../context/types";
import ProductImageWrapper from "../../BundlePreviewWrapper/ProductImageWrapper";
import useIsMobile from "../../../../hooks/useIsMobile";
import ProductSubscriptionsPreview from "../../BundlePreviewWrapper/ProductSubscriptionsPreview";
import { FreeGiftComponent } from "../../BundlePreviewWrapper/BundlePreviewFooter";
import Spinner from "../../../../components/Icons/Spinner";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { getProductId, MAYUR_TEST_STORE } from "../../../constants";
import BundleProgressBar from "../../BundleBuilder/BundleBuilderBundle/BundleProgressBar";
import { isDesktopUIForMixMatch } from "../../RenderOnStore/utilities";

const MixMatchSectionalBundle = (bundleConfig: MixMatchSectionalConfigInterface) => {
    return (
        !!bundleConfig && (
            <MixMatchSectionalPreview
                bundleConfig={{
                    ...bundleConfig,
                    ...(bundleConfig?.show_progress_bar && {
                        discount_options: bundleConfig.discount_options?.filter((d) => d.discountType !== "none"),
                    }),
                }}
                standAlone={true}
                appConfigContext={
                    {
                        currency: window.kefi_bundles.localization.currency,
                        money_format: window.kefi_bundles.money_format,
                        sft: window.kefi_bundles.sft,
                    } as TStoreContext
                }
            />
        )
    );
};

type Props_MixMatchSectionalPreview = {
    bundleConfig: MixMatchSectionalConfigInterface;
    standAlone?: boolean;
    appConfigContext: TStoreContext;
};

type TransformedVariant = {
    id: string;
    quantity: number;
};

const ProductCardShimmer = ({ type }: { type: "collection" | "specific" }) => {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100px",
                fontSize: "20px",
                color: "#999999",
            }}
        >
            {type === "collection" ? "Select Collection" : "Select product(s)"}
        </div>
    );
};

const ProductCard = ({
    product,
    index,
    bundleConfig,
    appConfigContext,
    standAlone = false,
    setTotalQuantities,
    discountType,
    discountValue,
    onVariantChange,
    finalProductsList,
    setFinalProductsList,
    setTotalPrice,
    setCompareAtPrice,
    isMobile,
    disableQuantityAddButton,
    bundleWrapperWidth,
}: {
    product: ProductObj;
    index: number;
    bundleConfig: MixMatchSectionalConfigInterface;
    appConfigContext: TStoreContext;
    standAlone: boolean;
    setTotalQuantities: React.Dispatch<React.SetStateAction<number>>;
    discountType: DiscountTypes;
    discountValue: number;
    onVariantChange: (index: number, variant: VariantsObj) => void;
    unavailableSelected: number[];
    setUnavailableSelected: React.Dispatch<
        React.SetStateAction<{
            [key: number]: number[];
        }>
    >;
    finalProductsList: any;
    setFinalProductsList: any;
    setTotalPrice: any;
    setCompareAtPrice: any;
    isMobile: boolean;
    type?: string;
    disableQuantityAddButton: boolean;
    bundleWrapperWidth: number;
}) => {
    const [, setDisPrice] = useState(0);
    const [origPrice, setOrigPrice] = useState(0);
    const [selectedVariants, setSelectedVariants] = useState<VariantsObj[]>(
        finalProductsList.find((p: ProductObj) => p.id === product.id)?.selectedVariants || [],
    );
    const { currency, money_format } = appConfigContext;
    console._log("setSelectedVariants", selectedVariants, finalProductsList);

    const isDesktopView = isDesktopUIForMixMatch(bundleWrapperWidth, bundleConfig?.bundle_type || "");

    useMemo(() => {
        setSelectedVariants(() => {
            return finalProductsList.find((p: ProductObj) => p.id === product.id)?.selectedVariants || [];
        });
    }, [product, finalProductsList]);

    const updatePrices = useCallback(() => {
        // Sum the prices of all selected variants
        const originalPrice = selectedVariants.reduce((total, variant) => {
            const price = bundleConfig?.apply_discount_on_compare_price ? +variant?.compareAtPrice : +variant.price;
            return total + price;
        }, 0);

        let discountPrice = originalPrice;
        const discount = discountValue || 0;

        switch (discountType) {
            case "percentage":
                // Discount is a percentage of the total price
                discountPrice -= discountPrice * (discount / 100);
                break;

            case "flat":
                // Flat discount applied directly to the total price
                discountPrice = discount < discountPrice ? discountPrice - discount : 0;
                break;

            case "fixed":
                // Fixed price after discount
                discountPrice = discount;
                break;

            case "none":
            default:
                // No discount
                discountPrice = originalPrice;
                break;
        }

        setDisPrice(Math.round(discountPrice * 100) / 100);
        setOrigPrice(Math.round(originalPrice * 100) / 100);
    }, [selectedVariants, discountType, discountValue, bundleConfig?.apply_discount_on_compare_price]);

    // Update selected variant and prices when variant changes
    useEffect(() => {
        product.selectedVariants = selectedVariants;
        onVariantChange(index, selectedVariants[selectedVariants.length - 1]);
        updatePrices();
        if (selectedVariants.length == 0) {
            return;
        }

        // if there is change in selected variant, update finalProductsList too
        setFinalProductsList((prev: any) => {
            const updatedList = prev
                .map((_product: any) => {
                    const { images, options, variants, ...restt } = _product;
                    console._log(typeof images, typeof options, typeof variants);

                    if (_product.id == product.id) {
                        return { ...restt, selectedVariants: selectedVariants };
                    }
                    return _product;
                })
                .filter((__product: any) => __product?.selectedVariants?.length !== 0); // Remove null products (those that have no variants left)

            return updatedList;
        });
    }, [selectedVariants, index, product, updatePrices]);

    // Handle product click for standalone or Shopify links
    const handleProductClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (standAlone && !window.location.pathname.includes(product.handle)) {
            const productURL = `https://${window.location.hostname}/products/${product.handle}`;
            window.open(productURL, "_blank");
        } else if (!window.location.pathname.includes(product.handle)) {
            const productURL = `https://${shopify.config.shop}/products/${product.handle}`;
            window.open(productURL, "_blank");
        }
    };
    const updateQuantity = (change: number) => {
        let updatedSv: VariantsObj[] = [];
        setSelectedVariants((prevSv) => {
            // selectedVariants updation
            updatedSv = [...prevSv];
            if (change == +1) {
                updatedSv.push(updatedSv[updatedSv?.length - 1]);
            } else {
                updatedSv.pop();
            }
            setFinalProductsList((prev: any) => {
                const updatedList = prev
                    .map((_product: any) => {
                        const { images, options, variants, ...restt } = _product;
                        console._log(typeof images, typeof options, typeof variants);

                        if (_product.id == product.id) {
                            return { ...restt, selectedVariants: updatedSv };
                        }
                        return _product;
                    })
                    .filter((__product: any) => __product?.selectedVariants?.length !== 0); // Remove null products (those that have no variants left)
                return updatedList;
            });
            return updatedSv;
        });
    };

    useEffect(() => {
        // Calculate total quantity and base prices
        if (finalProductsList.length === 0) {
            setTotalQuantities(0);
            setTotalPrice(0);
            setCompareAtPrice(0);
        } else {
            let tQty = 0;
            let tPrice = 0;
            let tComparePrice = 0;
            finalProductsList.forEach((fpl: any) => {
                tQty += fpl?.selectedVariants?.length || 0;
                fpl.selectedVariants?.forEach((sv: any) => {
                    tPrice += +sv?.price || 0;
                    tComparePrice += +sv?.compareAtPrice || +sv?.price || 0;
                });
            });
            setTotalPrice(tPrice);
            setTotalQuantities(tQty);
            setCompareAtPrice(tComparePrice);
        }
    }, [finalProductsList]);

    useEffect(() => {
        if (product.subscriptions && product.subscriptions.length > 0 && !product.oneTimePurchase) {
            product.isSubscribed = true;
        }
    }, [product, product.oneTimePurchase]);

    const renderPrice = () => {
        const availableVariant =
            product.variants?.find(
                (variant) => (variant.quantityAvailable && variant.quantityAvailable > 0) || variant.availableForSale,
            ) || product.variants?.[0];

        const productPrice = +availableVariant?.price || 0;
        const compareAtPrice = +availableVariant?.compareAtPrice || 0;

        const shouldShowCompareAtPrice =
            bundleConfig?.price_display_type === "both_price_and_compare_at_price" && compareAtPrice > productPrice;

        return (
            <div className={styles.productMetaRight}>
                {bundleConfig?.show_prices ? (
                    <>
                        {!bundleConfig?.apply_discount_on_compare_price ? (
                            <div
                                style={{
                                    width: "max-content",
                                    display: "flex",
                                    flexDirection: isDesktopView ? "row" : "column",
                                    alignItems: !isDesktopView ? "flex-end" : "",
                                    gap: isDesktopView ? "5px" : 0,
                                }}
                            >
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productPrice"
                                >
                                    <div
                                        style={{
                                            color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                            fontSize:
                                                bundleConfig?.bundles_design.highlighted_price_tag_size < 50
                                                    ? bundleConfig?.bundles_design.highlighted_price_tag_size
                                                    : 50,
                                            fontWeight:
                                                bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                            fontStyle:
                                                bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                        }}
                                    >
                                        {getPriceLabel(getFinalPrice("none", 0, productPrice), money_format, currency)}
                                    </div>
                                </InspectorEditableBlock>
                                {shouldShowCompareAtPrice && (
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="productCompareAtPrice"
                                    >
                                        <div
                                            style={{
                                                display: isDesktopView ? "flex" : "",
                                                alignItems: isDesktopView ? "center" : "",
                                                textDecoration: "line-through",
                                                marginBottom: "2px",
                                                color: bundleConfig?.bundles_design.product_price_color,
                                                fontSize:
                                                    bundleConfig?.bundles_design.product_price_size < 50
                                                        ? bundleConfig?.bundles_design.product_price_size
                                                        : 50,
                                                fontWeight:
                                                    bundleConfig?.bundles_design.product_price_style?.split(" ")[0],
                                                fontStyle:
                                                    bundleConfig?.bundles_design.product_price_style?.split(" ")[1],
                                            }}
                                        >
                                            {getPriceLabel(
                                                getFinalPrice("none", 0, compareAtPrice),
                                                money_format,
                                                currency,
                                            )}
                                        </div>
                                    </InspectorEditableBlock>
                                )}
                            </div>
                        ) : (
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                                <div
                                    style={{
                                        width: "max-content",
                                        color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                        fontSize:
                                            bundleConfig?.bundles_design.highlighted_price_tag_size < 50
                                                ? bundleConfig?.bundles_design.highlighted_price_tag_size
                                                : 50,
                                        fontWeight:
                                            bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                        fontStyle:
                                            bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                    }}
                                >
                                    {getPriceLabel(
                                        getFinalPrice("none", 0, compareAtPrice > 0 ? compareAtPrice : productPrice),
                                        money_format,
                                        currency,
                                    )}
                                </div>
                            </InspectorEditableBlock>
                        )}
                    </>
                ) : (
                    origPrice > 0 && (
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                            <div
                                style={{
                                    width: "max-content",
                                    color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                    fontSize:
                                        bundleConfig?.bundles_design.highlighted_price_tag_size < 50
                                            ? bundleConfig?.bundles_design.highlighted_price_tag_size
                                            : 50,
                                    fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                }}
                            >
                                {getPriceLabel(getFinalPrice("none", 0, origPrice), money_format, currency)}
                            </div>
                        </InspectorEditableBlock>
                    )
                )}
            </div>
        );
    };

    const renderAddCta = (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productAddButton">
            <button
                style={{
                    backgroundColor: bundleConfig?.bundles_design?.add_button_bg_color,
                    color: bundleConfig?.bundles_design?.add_button_text_color,
                    borderRadius: bundleConfig?.bundles_design?.add_button_border_radius,
                    border: `${bundleConfig?.bundles_design?.add_button_border_width}px solid ${bundleConfig?.bundles_design?.add_button_border_color}`,
                    fontSize: bundleConfig?.bundles_design?.add_button_text_size,
                    fontStyle: bundleConfig?.bundles_design?.add_button_text_style?.split(" ")[1],
                    ...(disableQuantityAddButton && { cursor: "not-allowed", opacity: 0.6 }),
                }}
                className={styles.addBtn}
                disabled={disableQuantityAddButton}
                onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    let curSub: any = {};
                    if (product?.subscriptions.length > 0 && product.subscriptions[0])
                        curSub = product?.subscriptions[0];
                    const availableVariant =
                        product.variants.find(
                            (vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
                        ) || product.variants[0];

                    setSelectedVariants([
                        {
                            ...availableVariant,
                            selling_plan:
                                product?.isSubscribed && curSub?.id
                                    ? curSub?.id
                                    : !product.oneTimePurchase
                                      ? product?.subscriptions &&
                                        product?.subscriptions.find((sub: any) => sub.checked === true)?.id
                                      : "",
                            ...(product?.isSubscribed && {
                                price:
                                    getFinalPrice(
                                        curSub?.discountType,
                                        curSub?.discountValue,
                                        +availableVariant.price,
                                    ) + "",
                            }),
                            copyPrice: availableVariant.price,
                        },
                    ]);

                    setFinalProductsList((prev: any) => {
                        const newArr = [...prev];
                        const { images, options, variants, ...restt } = product;
                        console._log(typeof images, typeof options, typeof variants);
                        newArr.push({
                            ...restt,
                            selectedVariants: [
                                {
                                    ...availableVariant,
                                    selling_plan:
                                        product?.isSubscribed && curSub?.id
                                            ? curSub?.id
                                            : !product.oneTimePurchase
                                              ? product?.subscriptions &&
                                                product?.subscriptions.find((sub: any) => sub.checked === true)?.id
                                              : "",
                                    ...(product?.isSubscribed && {
                                        price:
                                            getFinalPrice(
                                                curSub?.discountType,
                                                curSub?.discountValue,
                                                +availableVariant.price,
                                            ) + "",
                                    }),
                                    copyPrice: availableVariant.price,
                                },
                            ],
                        });
                        return newArr;
                    });
                }}
            >
                {bundleConfig.add_button || "Add"}
            </button>
        </InspectorEditableBlock>
    );

    const renderQuantity = (
        <>
            <div className={styles.subInfo}>
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productQuantityText">
                    <div
                        className={styles.qtyLabel}
                        style={{
                            color: bundleConfig?.bundles_design?.product_name_color,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {bundleConfig.quantity_text || "Qty"}
                    </div>
                </InspectorEditableBlock>

                <InspectorEditableBlock
                    bundleType={bundleConfig?.bundle_type}
                    settingKey="productAddButton"
                    wrapWithDiv={true}
                >
                    <div className={styles.qtyValue}>
                        <button
                            className={styles.quantityButton}
                            onClick={() => updateQuantity(-1)}
                            style={{
                                backgroundColor: bundleConfig?.bundles_design?.add_button_bg_color,
                                color: bundleConfig?.bundles_design?.add_button_text_color,
                            }}
                            disabled={
                                bundleConfig?.allow_pre_selected_products &&
                                bundleConfig?.pre_selected_products.some((p) => p.id === product.id) &&
                                selectedVariants?.length === 1
                            }
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ height: 9, width: 9 }} viewBox="0 0 10 2">
                                <path
                                    fill="currentColor"
                                    fill-rule="evenodd"
                                    d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </button>
                        <span
                            style={{
                                minWidth: 20,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontWeight: "600",
                                color: bundleConfig?.bundles_design?.product_name_color,
                            }}
                        >
                            {selectedVariants?.length}
                        </span>
                        <button
                            className={styles.quantityButton}
                            onClick={() => updateQuantity(+1)}
                            style={{
                                backgroundColor: bundleConfig?.bundles_design?.add_button_bg_color,
                                color: bundleConfig?.bundles_design?.add_button_text_color,
                            }}
                            disabled={disableQuantityAddButton}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ height: 9, width: 9 }} viewBox="0 0 10 10">
                                <path
                                    fill="currentColor"
                                    fill-rule="evenodd"
                                    d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </InspectorEditableBlock>
            </div>
        </>
    );

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product,
        bundleConfig: bundleConfig || {},
    });

    const isProductAvailable = useMemo(() => {
        return product.variants.some((vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale);
    }, [product]);

    return (
        <InspectorEditableBlock
            bundleType={bundleConfig?.bundle_type}
            settingKey="productCard"
            tagsKey={product.tags?.toString()}
            productId={getProductId(product?.id)}
        >
            {/* {index > 0 && <span className={styles.separator}></span>} */}

            <div
                style={{
                    padding: "14px 12px",
                    borderTopLeftRadius: index == 0 ? bundleConfig?.bundles_design?.border_radius : "0px",
                    borderTopRightRadius: index == 0 ? bundleConfig?.bundles_design?.border_radius : "0px",
                    borderBottomLeftRadius:
                        index == bundleConfig?.products.length - 1
                            ? bundleConfig?.bundles_design?.border_radius
                            : "0px",
                    borderBottomRightRadius:
                        index == bundleConfig?.products.length - 1
                            ? bundleConfig?.bundles_design?.border_radius
                            : "0px",
                    // borderRadius: bundleConfig?.bundles_design?.border_radius,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                }}
                className={styles.productCard}
            >
                <div className={styles.productInfoRight}>
                    <div className={styles.productMetaLeft}>
                        <ProductImageWrapper
                            product={{
                                ...product,
                                quantity: selectedVariants.length,
                            }}
                            handleProductClick={handleProductClick}
                            bundleConfig={bundleConfig}
                            isMobile={isMobile}
                            selectedVariants={selectedVariants}
                        />
                        <div
                            style={{
                                fontSize:
                                    bundleConfig?.bundles_design.product_name_size < 50
                                        ? bundleConfig?.bundles_design.product_name_size
                                        : 50,
                                color: bundleConfig?.bundles_design?.product_name_color,
                                fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                cursor: "pointer",
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 10,
                            }}
                        >
                            <div
                                onClick={handleProductClick}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    gap: "6px",
                                }}
                                className={styles.productTitle_Price}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="productTitle"
                                        wrapWithDiv={true}
                                    >
                                        <span
                                            style={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                lineHeight: "normal",
                                            }}
                                        >
                                            {product?.title}
                                        </span>
                                    </InspectorEditableBlock>
                                    {isDesktopView && renderPrice()}
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        {selectedVariants?.length > 0 ? (
                                            renderQuantity
                                        ) : isProductAvailable ? (
                                            renderAddCta
                                        ) : (
                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    color: "red",
                                                }}
                                            >
                                                {bundleConfig?.no_stock_text || "Out of stock!"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {!isDesktopView && renderPrice()}
                            </div>

                            {selectedVariants.length > 0 && (
                                <ProductSubscriptionsPreview
                                    product={product}
                                    setSelectedVariants={setSelectedVariants}
                                    key={product.id}
                                    bundleConfig={bundleConfig}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {product?.variants.length > 1 && (
                    <div
                        style={{
                            marginTop: selectedVariants?.length > 0 ? "10px" : "0px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "5px",
                            width: "100%",
                        }}
                    >
                        {selectedVariants?.map((_: any, i: number) =>
                            userShopname === "typsy-beauty.myshopify.com" ? (
                                <Swatches
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                />
                            ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                <CustomSwatches
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                    metafield={effectiveMetafield}
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    bundleConfig={bundleConfig}
                                />
                            ) : (
                                <VariantSelectorV2
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                    metafield={effectiveMetafield}
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    bundleConfig={bundleConfig}
                                />
                            ),
                        )}
                    </div>
                )}

                {product.isSubscribed && selectedVariants.length > 0 && (
                    <div style={{ width: "100%" }}>
                        <div style={{ margin: "5px 0" }}>
                            <span className={styles.separator}></span>
                        </div>

                        <SubscriptionSelector
                            product={product}
                            selectedVariants={selectedVariants}
                            setSelectedVariants={setSelectedVariants}
                        />
                    </div>
                )}
            </div>
        </InspectorEditableBlock>
    );
};

export const MixMatchSectionalPreview = ({
    bundleConfig,
    standAlone,
    appConfigContext,
}: Props_MixMatchSectionalPreview) => {
    const [totalPrice, setTotalPrice] = useState(0);
    const { currency, money_format, sft } = appConfigContext;
    const [totalQuantities, setTotalQuantities] = useState<number>(0);
    const [discountType, setDiscountType] = useState<DiscountTypes>("none");
    const [discountValue, setDiscountValue] = useState<number>(0);
    const [maxDiscountAllowed, setMaxDiscountAllowed] = useState<number | null>(null);
    const [compareAtPrice, setCompareAtPrice] = useState(0);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
    const activeSection = bundleConfig?.mix_match_sections[selectedSectionIndex];
    const [customFieldValue, setCustomFieldValue] = useState("");
    const [tierSuccessMessage, setTierSuccessMessage] = useState("");
    const [finalSuccessMessage, setFinalSuccessMessage] = useState("");
    const [collectionBasedProducts, setCollectionBasedProducts] = useState<ProductObj[]>([]);
    const [loadingMore, setLoadingMore] = useState(false);
    const [, setPage] = useState(1);
    const [pageInfo, setPageInfo] = useState<any>(null); // 🆕 state to store page info
    const fetchCount = 5;

    const bundleRef = useRef<HTMLDivElement>(null);
    const { isMobile } = useIsMobile();
    const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
    const [unavailableSelected, setUnavailableSelected] = useState<{
        [key: number]: number[];
    }>({});

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop || "";

    const bundleWrapperRef = useRef<HTMLDivElement>(null);
    // const [bundleWrapperWidth, setBundleWrapperWidth] = useState<number | null>(null);

    const bundleWrapperWidth = bundleWrapperRef.current?.offsetWidth || -1;

    const loadMoreCollectionProducts = async () => {
        if (loadingMore) return; // Prevent multiple fetches

        const gid = activeSection?.collections?.[0]?.id;
        const id = gid?.split("/").pop() || "";
        if (!id) return;

        const { products, pageInfo: newPageInfo } = await fetchCollectionProducts(
            id,
            sft,
            setLoadingMore,
            standAlone,
            fetchCount,
            pageInfo?.endCursor || null,
        );

        setCollectionBasedProducts((prevProducts) => [...prevProducts, ...products]);
        setPageInfo(newPageInfo);
        setLoadingMore(false);
    };

    // @updated: now checking selected section for collection type

    useEffect(() => {
        setPageInfo(null);
        setCollectionBasedProducts([]);
        setLoadingMore(false);
        setPage(1);
    }, [
        selectedSectionIndex,
        bundleConfig?.render_product_pages_type,
        activeSection?.product_selection,
        activeSection?.products,
        activeSection?.collections,
    ]);

    useEffect(() => {
        if (
            activeSection?.product_selection === "collection" &&
            activeSection.collections &&
            activeSection.collections?.length > 0 &&
            pageInfo === null
        ) {
            loadMoreCollectionProducts();
        }
    }, [pageInfo, activeSection?.product_selection, activeSection?.products, activeSection?.collections]);

    useEffect(() => {
        if (bundleConfig?.mix_match_sections?.length) {
            const validSection = bundleConfig.mix_match_sections.find((section: mix_match_section) => {
                if (section.product_selection === "collection") {
                    return section.collections && section.collections.length > 0;
                }
                if (section.product_selection === "specific") {
                    return section.products && section.products.length > 0;
                }
                return false;
            });

            const index = bundleConfig.mix_match_sections.indexOf(validSection!); //  added non-null assertion
            setSelectedSectionIndex(index !== -1 ? index : 0);
            setFinalProductsList([]);
        }
    }, [bundleConfig?.mix_match_sections]);

    useEffect(() => {
        const trigger = loadMoreTriggerRef.current;
        const scrollContainer = bundleRef.current;

        if (!trigger || !scrollContainer || !pageInfo?.hasNextPage) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMore) {
                    observer.disconnect();
                    loadMoreCollectionProducts();
                }
            },
            {
                root: scrollContainer,
                threshold: 0.2,
            },
        );

        observer.observe(trigger); // observe latest trigger

        return () => {
            observer.unobserve(trigger); // cleanup previous trigger
            observer.disconnect();
        };
    }, [pageInfo?.hasNextPage, loadMoreTriggerRef, collectionBasedProducts.length]); // 🆕 observe new trigger on update

    useEffect(() => {
        if (collectionBasedProducts?.length) {
            window?.localStorage.setItem("mm_s_handle", collectionBasedProducts[0]?.handle);
        }
    }, [collectionBasedProducts]);

    const getDiscountOption = () => {
        const isBundleTotal = bundleConfig?.tier_discount_basis === "bundle_total";
        const basePrice = bundleConfig?.apply_discount_on_compare_price ? compareAtPrice : totalPrice;
        const currentVal = isBundleTotal ? basePrice : totalQuantities;

        if (isBundleTotal) {
            // For bundle_total, find the highest tier reached
            const reversedOptions = [...(bundleConfig?.discount_options || [])].reverse();
            return reversedOptions.find((option) => currentVal >= option.quantity);
        }

        return bundleConfig?.discount_options.find((option) => {
            const isWithinQuantityRange =
                currentVal >= option.quantity &&
                (option.allowExtraItems || currentVal <= (option.maxQuantity || option.quantity));
            return isWithinQuantityRange;
        });
    };

    useEffect(() => {
        const discountOption = getDiscountOption();

        if (discountOption) {
            setDiscountType(discountOption.discountType || "none");
            setDiscountValue(discountOption.discountValue || 0);
            setMaxDiscountAllowed(discountOption.max_discount_allowed ?? null);
        } else {
            setDiscountType("none");
            setDiscountValue(0);
            setMaxDiscountAllowed(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalQuantities, bundleConfig?.discount_options, bundleConfig?.products]);

    const getNextTierOption = () => {
        const isBundleTotal = bundleConfig?.tier_discount_basis === "bundle_total";
        const basePrice = bundleConfig?.apply_discount_on_compare_price ? compareAtPrice : totalPrice;
        const currentVal = isBundleTotal ? basePrice : totalQuantities;
        return bundleConfig?.discount_options.find((option) => currentVal < option.quantity);
    };

    const getExcessQuantityMessage = (
        bundleConfig: MixMatchSectionalConfigInterface,
        totalQuantities: number,
        totalPrice: number,
        currency: string,
        money_format: string,
    ): { hasExcess: boolean; message: string; shouldResetProgress: boolean } => {
        const isBundleTotal = bundleConfig?.tier_discount_basis === "bundle_total";
        if (isBundleTotal) return { hasExcess: false, message: "", shouldResetProgress: false };
        const currentVal = isBundleTotal ? totalPrice : totalQuantities;

        const currentOption = bundleConfig?.discount_options.find((option) => {
            const isWithinQuantityRange =
                currentVal >= option.quantity &&
                (option.allowExtraItems || currentVal <= (option.maxQuantity || option.quantity));
            return isWithinQuantityRange;
        });

        // If there's a valid option, no excess
        if (currentOption) {
            return { hasExcess: false, message: "", shouldResetProgress: false };
        }

        // Find the tier that was exceeded
        const exceededOption = bundleConfig?.discount_options
            .slice()
            .reverse()
            .find((option) => {
                return totalQuantities > (option.maxQuantity || option.quantity) && !option.allowExtraItems;
            });

        if (exceededOption) {
            const maxAllowed = exceededOption.maxQuantity || exceededOption.quantity;
            const excessQuantity = totalQuantities - maxAllowed;

            const discountString = getDiscountValueWithSignString(
                exceededOption.discountType,
                exceededOption.discountValue || 0,
                currency,
                money_format,
            );

            const message =
                excessQuantity === 1
                    ? `Remove ${excessQuantity} product to get ${discountString} discount!`
                    : `Remove ${excessQuantity} product(s) to get ${discountString} discount!`;
            return {
                hasExcess: true,
                message: message,
                shouldResetProgress: true,
            };
        }

        return { hasExcess: false, message: "", shouldResetProgress: false };
    };

    const excessInfo = useMemo(
        () => getExcessQuantityMessage(bundleConfig, totalQuantities, totalPrice, currency, money_format),
        [bundleConfig, totalQuantities, totalPrice, currency, money_format],
    );

    // Effect to handle tier and final success messages
    useEffect(() => {
        const allOptions = bundleConfig?.discount_options || [];

        if (allOptions.length > 0) {
            const isBundleTotal = bundleConfig?.tier_discount_basis === "bundle_total";
            const currentVal = isBundleTotal ? totalPrice : totalQuantities;
            const lastTierQty = allOptions[allOptions.length - 1]?.quantity || 0;

            // Check if final tier reached and showFinalSuccessMessage is enabled
            if (currentVal >= lastTierQty && allOptions[0]?.showFinalSuccessMessage) {
                setFinalSuccessMessage(allOptions[0]?.finalSuccessMessage || "");
            } else {
                setFinalSuccessMessage("");
            }
        }

        const discountOption = getDiscountOption();
        const nextTierOption = getNextTierOption();

        if (discountOption) {
            setDiscountType(discountOption.discountType || "none");
            setDiscountValue(discountOption.discountValue || 0);
            setMaxDiscountAllowed(discountOption.max_discount_allowed ?? null);
        } else {
            setDiscountType("none");
            setDiscountValue(0);
            setMaxDiscountAllowed(null);
        }

        // Handle tier success message for next tier
        if (nextTierOption?.ShowTierSuccessMessage && nextTierOption?.TierSuccessMessage) {
            const isBundleTotal = bundleConfig?.tier_discount_basis === "bundle_total";
            const currentVal = isBundleTotal ? totalPrice : totalQuantities;
            const remaining = nextTierOption.quantity - currentVal;
            const discountString = getDiscountValueWithSignString(
                nextTierOption.discountType,
                nextTierOption.discountValue || 0,
                currency,
                money_format,
            );

            let msg = nextTierOption.TierSuccessMessage;

            if (isBundleTotal) {
                const remainingStr = getPriceLabel(remaining, money_format, currency, true, "string") as string;
                msg = msg.replace(/{remainingAmount}/g, remainingStr);
                msg = msg.replace(/{noOfRemainingProducts}/g, remainingStr);
            } else {
                const remainingStr = remaining.toString();
                msg = msg.replace(/{noOfRemainingProducts}/g, remainingStr);
                msg = msg.replace(/{remainingAmount}/g, remainingStr);
            }

            msg = msg.replace(/{discount}/g, (discountString || "") as string);

            setTierSuccessMessage(msg);
        } else {
            setTierSuccessMessage("");
        }
    }, [
        totalQuantities,
        bundleConfig?.discount_options,
        currency,
        money_format,
        totalPrice,
        compareAtPrice,
        bundleConfig?.apply_discount_on_compare_price,
    ]);

    const [freeGiftSelectedVariants, setFreeGiftSelectedVariants] = useState<VariantsObj[]>([]);

    const getTransformedPreSelectedProducts = () => {
        if (bundleConfig?.allow_pre_selected_products && bundleConfig?.pre_selected_products?.length > 0) {
            const allProductsList =
                bundleConfig?.mix_match_sections?.flatMap((section) => {
                    return section?.products?.map((p) => p.id);
                }) || [];
            const allCollectionsList =
                bundleConfig?.mix_match_sections?.flatMap((section) => {
                    return section?.collections?.map((c) => c.id);
                }) || [];

            return bundleConfig.pre_selected_products.map((product) => {
                // @ts-expect-error required
                const hasMatchingCollection = product?.collections?.some((c: any) => allCollectionsList.includes(c));

                if (!allProductsList.includes(product.id) && !hasMatchingCollection) {
                    return product;
                }

                const availableVariant = product.variants.find(
                    (variant) =>
                        (variant.quantityAvailable && variant.quantityAvailable > 0) || variant.availableForSale,
                );

                if (!availableVariant) {
                    return product;
                }

                return {
                    ...product,
                    selling_plan: "",
                    selectedVariants: [
                        {
                            ...availableVariant,
                        },
                    ],
                };
            });
        }
        return [];
    };

    const [finalProductsList, setFinalProductsList] = useState<ProductObj[]>(getTransformedPreSelectedProducts());

    useEffect(() => {
        const tempArr = getTransformedPreSelectedProducts();
        setFinalProductsList(tempArr);
    }, [
        bundleConfig?.allow_pre_selected_products,
        bundleConfig?.pre_selected_products,
        bundleConfig?.mix_match_sections,
    ]);

    const [disableAddToCartButton, setDisableAddToCartButton] = useState(false);

    const getBundleConditionMessage = (): string => {
        if (!bundleConfig?.allow_bundle_condition) return "";

        const conditionType = bundleConfig.bundle_condition_type;
        const limit = bundleConfig.product_count_limit;

        if (conditionType === "min_products") {
            if (totalQuantities < limit) {
                return bundleConfig.tooltip_error_message.replace("{productCount}", String(limit - totalQuantities));
            }
        } else if (conditionType === "max_products") {
            if (totalQuantities >= limit) {
                const msg = bundleConfig.tooltip_error_message;

                if (!msg) return "";

                const value = limit - totalQuantities === 0 ? limit : Math.max(0, limit - totalQuantities);

                return msg.includes("{productCount}") ? msg.replace("{productCount}", String(value)) : msg;
            }
        } else if (conditionType === "exact_products") {
            if (totalQuantities < limit) {
                return bundleConfig.tooltip_error_message.replace("{productCount}", String(limit - totalQuantities));
            } else if (totalQuantities > limit) {
                const msg = bundleConfig.tooltip_error_message;

                if (!msg) return "";

                const value = limit - totalQuantities === 0 ? limit : Math.max(0, limit - totalQuantities);

                return msg.includes("{productCount}") ? msg.replace("{productCount}", String(value)) : msg;
            }
        }
        return "";
    };

    const bundleConditionMessage = getBundleConditionMessage();

    const updatedBundleConfig = {
        ...bundleConfig,
        products: [
            ...finalProductsList,
            ...freeGiftSelectedVariants.map((variant) => ({
                selectedVariants: [variant], // Wrapping each variant in selectedVariants array
            })),
        ],
        mnmSectionalMessage: customFieldValue,
        discount_type: discountType,
        discount_value: discountValue,
        max_allowed_discount: maxDiscountAllowed,
        disableAddToCartButton:
            ([
                "quickstart-f02489ba.myshopify.com",
                // "msg-september2025-entwicklungsumgebung.myshopify.com",
                // "mein-schoener-garten-shop.myshopify.com",
                MAYUR_TEST_STORE,
            ].includes(userShopname) &&
                !getDiscountOption()) ||
            disableAddToCartButton,
        tooltipMessage: bundleConfig?.allow_bundle_condition
            ? bundleConfig.tooltip_error_message.replace("{productCount}", String(bundleConfig.product_count_limit))
            : "",
        bundleConditionMessage: bundleConditionMessage,
    };

    const getTransformedVariants = (finalProductsList: ProductObj[]): TransformedVariant[] => {
        const variantMap: Record<string, TransformedVariant> = {};

        finalProductsList.forEach((product) => {
            product.selectedVariants.forEach((variant) => {
                const id = variant.id;
                if (variantMap[id]) {
                    variantMap[id].quantity += 1; // increment quantity if already exists
                } else {
                    variantMap[id] = { id, quantity: 1 }; // add new variant with quantity 1
                }
            });
        });

        return Object.values(variantMap);
    };

    useEffect(() => {
        const hasActiveFreeGiftOption = bundleConfig?.discount_options?.some(
            (option) =>
                totalQuantities >= option.quantity &&
                (option.allowExtraItems || totalQuantities <= option.maxQuantity) &&
                option.allow_free_gifts &&
                option.free_gifts &&
                option.free_gift_text,
        );

        if (!hasActiveFreeGiftOption) {
            setFreeGiftSelectedVariants([]);
        }
    }, [bundleConfig?.discount_options, totalQuantities]);

    const selectedProductsCount = finalProductsList?.reduce((acc, p) => {
        return acc + p.selectedVariants.length;
    }, 0);

    const disableQuantityAddButton = bundleConfig?.allow_bundle_condition
        ? (bundleConfig.bundle_condition_type === "max_products" ||
              bundleConfig.bundle_condition_type === "exact_products") &&
          selectedProductsCount >= bundleConfig.product_count_limit
        : false;

    useEffect(() => {
        if (bundleConfig?.allow_bundle_condition) {
            const conditionType = bundleConfig.bundle_condition_type;
            const limit = bundleConfig.product_count_limit;

            if (conditionType === "min_products") {
                setDisableAddToCartButton(selectedProductsCount < limit);
            } else if (conditionType === "max_products") {
                setDisableAddToCartButton(selectedProductsCount > limit);
            } else if (conditionType === "exact_products") {
                setDisableAddToCartButton(selectedProductsCount !== limit);
            }
        } else {
            setDisableAddToCartButton(false);
        }
    }, [selectedProductsCount]);

    if (!bundleConfig) return null;
    return (
        <div ref={bundleWrapperRef}>
            <BundlePreviewWrapper
                // @ts-expect-error required
                bundleConfig={updatedBundleConfig}
                standAlone={standAlone}
                totalPrice={bundleConfig?.apply_discount_on_compare_price ? compareAtPrice : totalPrice}
                currency={currency}
                transformedVariants={getTransformedVariants(finalProductsList)}
                money_format={money_format}
                compareAtPriceTotal={compareAtPrice}
            >
                <>
                    {/* Top tabs for quantity ranges */}
                    {bundleConfig?.show_progress_bar ? (
                        bundleConfig?.discount_options?.length > 0 && (
                            <>
                                {excessInfo.hasExcess && (
                                    <div
                                        style={{
                                            padding: "10px 15px",
                                            color:
                                                bundleConfig?.bundles_design?.discount_tabs_discount_label_color ||
                                                "#2e7d32",
                                            fontSize:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_size || 14,
                                            fontWeight:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                    " ",
                                                )[0] || "500",
                                            fontStyle:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                    " ",
                                                )[1] || "normal",
                                            textAlign: "center",
                                            lineHeight: "1.4",
                                        }}
                                    >
                                        {excessInfo.message}
                                    </div>
                                )}

                                {!excessInfo.hasExcess && tierSuccessMessage && (
                                    <div
                                        style={{
                                            padding: "10px 15px",
                                            color:
                                                bundleConfig?.bundles_design?.discount_tabs_discount_label_color ||
                                                "#2e7d32",
                                            fontSize:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_size || 14,
                                            fontWeight:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                    " ",
                                                )[0] || "500",
                                            fontStyle:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                    " ",
                                                )[1] || "normal",
                                            textAlign: "center",
                                            lineHeight: "1.4",
                                        }}
                                    >
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: tierSuccessMessage,
                                            }}
                                        />
                                    </div>
                                )}

                                {!excessInfo.hasExcess && finalSuccessMessage && (
                                    <div
                                        style={{
                                            padding: "10px 15px",
                                            color:
                                                bundleConfig?.bundles_design?.discount_tabs_discount_label_color ||
                                                "#2e7d32",
                                            fontSize:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_size || 14,
                                            fontWeight:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                    " ",
                                                )[0] || "500",
                                            fontStyle:
                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                    " ",
                                                )[1] || "normal",
                                            textAlign: "center",
                                            lineHeight: "1.4",
                                        }}
                                    >
                                        {finalSuccessMessage}
                                    </div>
                                )}

                                <div
                                    style={{
                                        width: "95%",
                                        minHeight: "32px",
                                        padding: "8px 0",
                                    }}
                                >
                                    <BundleProgressBar
                                        bundleConfig={bundleConfig}
                                        totalPrice={totalPrice}
                                        totalQuantity={excessInfo.shouldResetProgress ? 0 : totalQuantities}
                                        currency={currency}
                                        money_format={money_format}
                                    />
                                </div>
                            </>
                        )
                    ) : (
                        <div className={styles.quantityTabs}>
                            {bundleConfig?.discount_options?.map((option, index) => {
                                const isBundleTotal = bundleConfig?.tier_discount_basis === "bundle_total";
                                const currentVal = isBundleTotal ? totalPrice : totalQuantities;

                                const isLastOption = index === bundleConfig.discount_options.length - 1;
                                const nextOption = bundleConfig.discount_options[index + 1];

                                const isActive = isBundleTotal
                                    ? currentVal >= option.quantity &&
                                      (isLastOption || currentVal < nextOption.quantity)
                                    : currentVal >= option.quantity &&
                                      (option.allowExtraItems || currentVal <= (option.maxQuantity || option.quantity));
                                let label = "";

                                if (isBundleTotal) {
                                    const spendAmount = getPriceLabel(
                                        option.quantity,
                                        money_format,
                                        currency,
                                        true,
                                        "string",
                                    );
                                    label = `${spendAmount}+`;
                                } else if (option.addRange) {
                                    if (option.allowExtraItems && option.quantity === option.maxQuantity) {
                                        label = `${option.quantity}+ ${bundleConfig?.option_items_text || "items"}`;
                                    } else if (option.quantity === option.maxQuantity) {
                                        label = `${option.quantity} ${bundleConfig?.option_items_text || "items"}`;
                                    } else {
                                        label = `${option.quantity}-${option.maxQuantity} ${
                                            bundleConfig?.option_items_text || "items"
                                        }`;
                                    }
                                } else {
                                    if (option.allowExtraItems && option.quantity === option.maxQuantity) {
                                        label = `${option.quantity}+ ${bundleConfig?.option_items_text || "items"}`;
                                    } else {
                                        label = `${option.quantity} ${bundleConfig?.option_items_text || "items"}`;
                                    }
                                }

                                const getDiscountLabel = () => {
                                    if (option.discountValue < 0) {
                                        return "";
                                    }

                                    switch (option.discountType) {
                                        case "percentage":
                                            if (option.discountValue == 0) {
                                                return "";
                                            }
                                            if (option.max_discount_allowed) {
                                                return (
                                                    <>
                                                        <span>
                                                            {option.discountValue}%{" "}
                                                            {bundleConfig?.option_off_text || "Off"}{" "}
                                                        </span>
                                                        ({bundleConfig.option_upto_text || "upto"}&nbsp;
                                                        {getPriceLabel(
                                                            getFinalPrice("none", 0, option.max_discount_allowed),
                                                            money_format,
                                                            currency,
                                                        )}
                                                        )
                                                    </>
                                                );
                                            }
                                            if (option.discountValue == 100) {
                                                return bundleConfig?.free_text || "FREE";
                                            }
                                            return `${option.discountValue}% ${bundleConfig?.option_off_text || "Off"}`;

                                        case "fixed":
                                            if (option.discountValue == 0) {
                                                return bundleConfig?.free_text || "FREE";
                                            }
                                            return getDiscountValueWithSign(
                                                option.discountType,
                                                option.discountValue,
                                                currency,
                                                money_format,
                                                bundleConfig?.footer_text,
                                            );
                                        case "flat":
                                            if (option.discountValue == 0) {
                                                return "";
                                            }
                                            return getDiscountValueWithSign(
                                                option.discountType,
                                                option.discountValue,
                                                currency,
                                                money_format,
                                                bundleConfig?.footer_text,
                                            );

                                        default:
                                            return "";
                                    }
                                };

                                return (
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey={`quantityTab`}
                                    >
                                        <div
                                            key={option.id + index}
                                            className={`${styles.tab} ${isActive ? styles.active : ""}`}
                                            style={{
                                                borderWidth:
                                                    bundleConfig?.bundles_design?.discount_tabs_border_width ?? 2,
                                                borderRadius: bundleConfig?.bundles_design?.discount_tabs_border_radius,
                                                backgroundColor: isActive
                                                    ? bundleConfig?.bundles_design?.discount_tabs_selected_bg_color
                                                    : bundleConfig?.bundles_design?.discount_tabs_bg_color,
                                                borderColor: isActive
                                                    ? bundleConfig?.bundles_design?.discount_tabs_selected_border_color
                                                    : bundleConfig?.bundles_design?.discount_tabs_border_color,
                                            }}
                                        >
                                            <InspectorEditableBlock
                                                bundleType={bundleConfig?.bundle_type}
                                                settingKey={`quantityLabel`}
                                            >
                                                <span
                                                    className={styles.quantityLabel}
                                                    style={{
                                                        fontSize: `${
                                                            bundleConfig?.bundles_design
                                                                ?.discount_tabs_quantity_label_size < 50
                                                                ? bundleConfig?.bundles_design
                                                                      ?.discount_tabs_quantity_label_size
                                                                : 50
                                                        }px`,
                                                        color: isActive
                                                            ? bundleConfig?.bundles_design
                                                                  ?.discount_tabs_selected_text_color
                                                            : bundleConfig?.bundles_design
                                                                  ?.discount_tabs_discount_label_color,
                                                        fontWeight:
                                                            bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                                " ",
                                                            )[1],
                                                    }}
                                                >
                                                    {label}
                                                </span>
                                            </InspectorEditableBlock>
                                            <InspectorEditableBlock
                                                bundleType={bundleConfig?.bundle_type}
                                                settingKey={`discountValue`}
                                            >
                                                <span
                                                    className={styles.discountLabel}
                                                    style={{
                                                        fontSize: `${
                                                            bundleConfig?.bundles_design
                                                                ?.discount_tabs_discount_label_size < 50
                                                                ? bundleConfig?.bundles_design
                                                                      ?.discount_tabs_discount_label_size
                                                                : 50
                                                        }px`,
                                                        color: isActive
                                                            ? bundleConfig?.bundles_design
                                                                  ?.discount_tabs_selected_text_color
                                                            : bundleConfig?.bundles_design
                                                                  ?.discount_tabs_discount_label_color,
                                                        fontWeight:
                                                            bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                                " ",
                                                            )[1],
                                                    }}
                                                >
                                                    {getDiscountLabel()}

                                                    {option?.allow_free_gifts &&
                                                        option?.free_gifts &&
                                                        option?.free_gifts?.length > 0 && (
                                                            <> {bundleConfig?.offer_free_gift_text || "+ Free Gift"}</>
                                                        )}
                                                </span>
                                            </InspectorEditableBlock>
                                        </div>
                                    </InspectorEditableBlock>
                                );
                            })}
                        </div>
                    )}
                    {/* Top sections */}
                    {bundleConfig?.mix_match_sections?.length > 0 && (
                        <>
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey={`sectionTitle`}>
                                <div
                                    className={styles.SectionsSubtitle}
                                    style={{
                                        fontSize: `${
                                            bundleConfig?.bundles_design?.section_tabs_title_size < 50
                                                ? bundleConfig?.bundles_design?.section_tabs_subtitle_size
                                                : 50
                                        }px`,
                                        color: bundleConfig?.bundles_design?.section_tabs_subtitle_color,
                                        fontWeight:
                                            bundleConfig?.bundles_design?.section_tabs_title_style?.split(" ")[0],
                                        fontStyle:
                                            bundleConfig?.bundles_design?.section_tabs_title_style?.split(" ")[1],
                                    }}
                                >
                                    {activeSection?.subtitle}
                                </div>
                            </InspectorEditableBlock>

                            <div className={styles.tabContainer}>
                                {bundleConfig?.mix_match_sections.map((section: mix_match_section, index) => (
                                    <>
                                        <InspectorEditableBlock
                                            bundleType={bundleConfig?.bundle_type}
                                            settingKey={`sectionTab`}
                                        >
                                            <div
                                                key={section.id + index}
                                                className={`${styles.tabItem} ${
                                                    selectedSectionIndex === index ? styles.tabItemActive : ""
                                                }`}
                                                style={{
                                                    borderRadius:
                                                        bundleConfig?.bundles_design?.section_tabs_border_radius,

                                                    border:
                                                        selectedSectionIndex === index
                                                            ? `2px solid ${bundleConfig?.bundles_design?.section_tabs_title_color}`
                                                            : undefined,
                                                    padding: standAlone ? "1px 16px" : "",
                                                    backgroundColor:
                                                        selectedSectionIndex === index
                                                            ? bundleConfig?.bundles_design?.section_tabs_bg_color
                                                            : bundleConfig?.bundles_design
                                                                  ?.section_tabs_unselected_bg_color,
                                                }}
                                                onClick={() => {
                                                    setSelectedSectionIndex(index);
                                                }}
                                            >
                                                <span
                                                    className={styles.tabTitle}
                                                    style={{
                                                        fontSize: `${
                                                            bundleConfig?.bundles_design?.section_tabs_title_size < 50
                                                                ? bundleConfig?.bundles_design?.section_tabs_title_size
                                                                : 50
                                                        }px`,
                                                        color:
                                                            selectedSectionIndex === index
                                                                ? bundleConfig?.bundles_design?.section_tabs_title_color
                                                                : bundleConfig?.bundles_design
                                                                      ?.section_tabs_unselected_title_color,
                                                        fontWeight:
                                                            bundleConfig?.bundles_design?.section_tabs_title_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            bundleConfig?.bundles_design?.section_tabs_title_style?.split(
                                                                " ",
                                                            )[1],
                                                    }}
                                                >
                                                    {section.title || `Section ${index + 1}`}
                                                </span>
                                            </div>
                                        </InspectorEditableBlock>
                                    </>
                                ))}
                            </div>
                        </>
                    )}

                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey={`productCard`}>
                        <div
                            className={styles.products}
                            ref={bundleRef}
                            style={{
                                borderRadius: bundleConfig?.bundles_design?.border_radius,
                                border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                backgroundColor: bundleConfig?.bundles_design.product_background_color,
                                maxHeight: "435px",
                                overflow: "auto",
                                WebkitOverflowScrolling: "auto",
                                overscrollBehavior: "auto",
                            }}
                        >
                            {activeSection?.product_selection === "collection" && collectionBasedProducts.length > 0 ? (
                                <>
                                    {collectionBasedProducts.map((product, index) => (
                                        <ProductCard
                                            key={product?.id + product?.variants[0]?.id}
                                            index={index}
                                            product={product}
                                            bundleConfig={bundleConfig}
                                            appConfigContext={appConfigContext}
                                            standAlone={standAlone || false}
                                            setTotalQuantities={setTotalQuantities}
                                            discountType={discountType}
                                            discountValue={discountValue}
                                            onVariantChange={() => {}}
                                            unavailableSelected={unavailableSelected[index] || []}
                                            setUnavailableSelected={setUnavailableSelected}
                                            finalProductsList={finalProductsList}
                                            setFinalProductsList={setFinalProductsList}
                                            setTotalPrice={setTotalPrice}
                                            setCompareAtPrice={setCompareAtPrice}
                                            isMobile={isMobile}
                                            type="collection"
                                            disableQuantityAddButton={disableQuantityAddButton}
                                            bundleWrapperWidth={bundleWrapperWidth}
                                        />
                                    ))}
                                </>
                            ) : activeSection?.product_selection == "specific" &&
                              activeSection?.products &&
                              activeSection?.products?.length > 0 ? (
                                <>
                                    {activeSection?.products.map((product, index) => (
                                        <ProductCard
                                            key={product?.id + product?.variants[0]?.id}
                                            index={index}
                                            product={product}
                                            bundleConfig={bundleConfig}
                                            appConfigContext={appConfigContext}
                                            standAlone={standAlone || false}
                                            setTotalQuantities={setTotalQuantities}
                                            discountType={discountType}
                                            discountValue={discountValue}
                                            onVariantChange={() => {}}
                                            unavailableSelected={unavailableSelected[index] || []}
                                            setUnavailableSelected={setUnavailableSelected}
                                            finalProductsList={finalProductsList}
                                            setFinalProductsList={setFinalProductsList}
                                            setTotalPrice={setTotalPrice}
                                            setCompareAtPrice={setCompareAtPrice}
                                            isMobile={isMobile}
                                            disableQuantityAddButton={disableQuantityAddButton}
                                            bundleWrapperWidth={bundleWrapperWidth}
                                        />
                                    ))}
                                </>
                            ) : (
                                <ProductCardShimmer
                                    type={
                                        bundleConfig?.mixmatch_product_selection === "collection"
                                            ? "collection"
                                            : "specific"
                                    }
                                />
                            )}
                            <div className={styles.loadMoreWrapper}>
                                {loadingMore ? (
                                    <div
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignContent: "center",
                                            padding: 10,
                                        }}
                                    >
                                        <Spinner color={"#939393"} size={50} />
                                        <span
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                fontSize: 12,
                                                color: "#939393",
                                            }}
                                        >
                                            Loading more products...
                                        </span>
                                    </div>
                                ) : (
                                    collectionBasedProducts.length > 0 &&
                                    pageInfo?.hasNextPage && (
                                        <span
                                            ref={loadMoreTriggerRef}
                                            style={{
                                                width: "100%",

                                                display: "flex",

                                                height: "10px",

                                                visibility: "hidden",
                                            }}
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    </InspectorEditableBlock>

                    {bundleConfig?.discount_options?.map((option, index) => {
                        const isBundleTotal = bundleConfig?.tier_discount_basis === "bundle_total";
                        const basePrice = bundleConfig?.apply_discount_on_compare_price ? compareAtPrice : totalPrice;
                        const currentVal = isBundleTotal ? basePrice : totalQuantities;

                        const isLastOption = index === (bundleConfig?.discount_options || []).length - 1;
                        const nextOption = (bundleConfig?.discount_options || [])[index + 1];

                        const isActive = isBundleTotal
                            ? currentVal >= option.quantity &&
                              (isLastOption || (nextOption && currentVal < nextOption.quantity))
                            : currentVal >= option.quantity &&
                              (option.allowExtraItems || currentVal <= (option.maxQuantity || option.quantity));

                        return (
                            isActive &&
                            option.allow_free_gifts &&
                            option.free_gifts &&
                            option.free_gift_text && (
                                <div style={{ marginTop: 15 }}>
                                    <FreeGiftComponent
                                        products={option?.free_gifts || []}
                                        bundleConfig={
                                            {
                                                ...bundleConfig,
                                                free_gift_text: option?.free_gift_text || "",
                                                bundles_design: {
                                                    ...bundleConfig.bundles_design,
                                                    add_to_cart_border_radius:
                                                        bundleConfig?.bundles_design.border_radius,
                                                },
                                            } as any
                                        }
                                        standAlone={standAlone}
                                        targetForm={""}
                                        setFreeGiftSelectedVariants={setFreeGiftSelectedVariants}
                                        money_format={money_format}
                                        currency={currency}
                                    />
                                </div>
                            )
                        );
                    })}

                    {(bundleConfig?.render_product_pages_type == true || standAlone) &&
                        bundleConfig?.isCustomFieldVisible && (
                            <div className={styles.customFieldWrapper}>
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="customFieldTitle"
                                >
                                    <label
                                        className={styles.customLabel}
                                        style={{
                                            color: bundleConfig?.bundles_design?.custom_field_title_color,
                                        }}
                                    >
                                        {bundleConfig.custom_fields.title || "Send a Message"}
                                    </label>
                                </InspectorEditableBlock>
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="customFieldValue"
                                >
                                    <textarea
                                        style={{
                                            background: bundleConfig?.bundles_design?.custom_field_bg_color,
                                            color: bundleConfig?.bundles_design?.custom_field_message_color,
                                            // @ts-expect-error required
                                            "--placeholder-color":
                                                bundleConfig?.bundles_design?.custom_field_message_color,
                                        }}
                                        className={styles.customTextarea}
                                        placeholder={bundleConfig.custom_fields.placeholder || "Enter Message Here"}
                                        value={customFieldValue}
                                        onChange={(e) => setCustomFieldValue(e.target.value)}
                                    />
                                </InspectorEditableBlock>
                            </div>
                        )}
                </>
            </BundlePreviewWrapper>
        </div>
    );
};
export default MixMatchSectionalBundle;
